import React, { useState, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  Eye,
  Plus,
  Trash2,
  Edit2,
  MessageSquare,
  User,
  Quote,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import FormField from "../../components/ui/FormField";
import { useAuth } from "../../auth/AuthContext";
import { Textarea } from "../../components/ui/Textarea";
import { getWebsiteSettings, createTestimonial, updateTestimonial, deleteTestimonial, getTestimonials, getSingleTestimonial } from "../../services/websitesetting";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

interface Testimonial {
  id: number;
  client: string;
  feedback: string;
}

const TestimonialsSection = () => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitText, setSubmitText] = useState("Saving...");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<number | null>(null);
  const { subdomain } = useOutletContext<{ subdomain: string }>();
  const toast = useToast();

  const [clientName, setClientName] = useState("");
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await getTestimonials();
        if (response.data) {
          setTestimonials(response.data);
        }
      } catch (err: any) {
        console.error("fetchData ERROR:", err);
        console.error("fetchData Response:", err.response);
        console.error("fetchData Response Data:", err.response?.data);
        const status = err.response?.status;
        if (status === 404) {
          toast.error("Testimonials not found. Starting fresh.");
        } else {
          toast.error("Failed to load testimonials. Please refresh.");
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOpenModal = async (testimonial?: Testimonial) => {
    setIsModalOpen(true);
    setErrors({});

    if (testimonial) {
      setEditingId(testimonial.id);
      setClientName(testimonial.client);
      setFeedback(testimonial.feedback);

      // Fetch fresh data from server in background
      try {
        const response = await getSingleTestimonial(testimonial.id);
        if (response.data) {
          setClientName(response.data.client);
          setFeedback(response.data.feedback);
        }
      } catch (err) {
        console.error("Failed to fetch single testimonial:", err);
      }
    } else {
      setEditingId(null);
      setClientName("");
      setFeedback("");
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!clientName.trim()) {
      newErrors.clientName = "Client name is required";
    } else if (clientName.length < 2) {
      newErrors.clientName = "Client name must be at least 2 characters";
    } else if (clientName.length > 100) {
      newErrors.clientName = "Client name cannot exceed 100 characters";
    }

    if (!feedback.trim()) {
      newErrors.feedback = "Feedback is required";
    } else if (feedback.length < 10) {
      newErrors.feedback = "Feedback must be at least 10 characters";
    } else if (feedback.length > 500) {
      newErrors.feedback = "Feedback cannot exceed 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors.");
      return;
    }

    setIsSubmitting(true);
    setSubmitText(editingId ? "Updating testimonial..." : "Adding testimonial...");
    setErrors({});

    try {
      if (editingId) {
        await updateTestimonial(editingId, { client: clientName, feedback });
        setTestimonials((prev) =>
          prev.map((t) =>
            t.id === editingId ? { ...t, client: clientName, feedback } : t
          )
        );
        toast.success("Testimonial updated successfully!");
      } else {
        const response = await createTestimonial({ client: clientName, feedback });
        const newTestimonial = response.data;
        setTestimonials((prev) => [...prev, newTestimonial]);
        toast.success("Testimonial added successfully!");
      }
      setIsModalOpen(false);
    } catch (error: any) {
      console.error("handleSave ERROR:", error);
      console.error("handleSave Response:", error.response);

      if (error.type === "serverValidation") {
        setErrors(error.errors);
        toast.error("Validation failed. Please check the fields.");
      } else {
        const status = error.response?.status;
        let userMessage = "Failed to save testimonial.";

        if (status === 404) {
          userMessage = "Testimonial configuration not found. Please refresh the page.";
        } else if (status === 403) {
          userMessage = "You don't have permission to modify testimonials.";
        } else if (status === 413) {
          userMessage = "The testimonial data is too large.";
        } else if (status === 500) {
          userMessage = "Server error occurred. Please try again.";
        } else if (error.message === "Network Error") {
          userMessage = "Connection lost. Please check your internet and try again.";
        }

        toast.error(userMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    setTestimonialToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!testimonialToDelete) return;

    setIsSubmitting(true);
    setSubmitText("Deleting testimonial...");
    try {
      await deleteTestimonial(testimonialToDelete);

      setTestimonials((prev) => prev.filter((t) => t.id !== testimonialToDelete));
      toast.success("Testimonial deleted successfully!");
    } catch (error: any) {
      console.error("handleDelete ERROR:", error);
      console.error("handleDelete Response:", error.response);

      const status = error.response?.status;
      let userMessage = "Failed to delete testimonial.";

      if (status === 404) {
        userMessage = "Testimonial not found.";
      } else if (status === 403) {
        userMessage = "You don't have permission to delete testimonials.";
      } else if (status === 500) {
        userMessage = "Server error occurred. Please try again.";
      } else if (error.message === "Network Error") {
        userMessage = "Connection lost. Please check your internet.";
      }

      toast.error(userMessage);
    } finally {
      setIsSubmitting(false);
      setTestimonialToDelete(null);
    }
  };

  return (
    <>
      {(isLoading || isSubmitting) && (
        <GlobalLoader
          initialPageLoad={isLoading}
          fullScreen={isSubmitting}
          description={isSubmitting ? submitText : "Loading testimonials..."}
        />
      )}
      <div className="space-y-5">
        {/* Header Card */}
        <PageHeader
          title="Testimonials"
          description="Showcase feedback from your happy clients"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={
            <ViewWebsiteButton subdomain={subdomain} />
          }
        />

        {/* Main Content */}
        <WebsiteSectionCard
          title="Client Reviews"
          icon={MessageSquare}
          actions={
            <Button
              onClick={() => handleOpenModal()}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20 text-[10px] lg:text-xs flex items-center gap-1.5 px-2.5 py-1.5 h-auto"
            >
              <Plus size={14} />
              <span className="hidden xs:inline">Add Testimonial</span>
              <span className="xs:hidden">Add</span>
            </Button>
          }
        >
          <div className="space-y-4">
            <InfoContainer
              message="Display feedback and testimonials from your clients to build trust and credibility."
              iconSize={13}
            />

            {testimonials.length === 0 ? (
              <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Quote className="text-[#885ABB]" size={20} />
                </div>
                <h3 className="text-sm font-medium text-gray-900">
                  No testimonials yet
                </h3>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  Add your first client review to build trust
                </p>
                <Button
                  onClick={() => handleOpenModal()}
                  className="bg-[#885ABB] hover:bg-[#7748A4] text-white text-xs"
                >
                  Add Testimonial
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {testimonials.map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className="group relative bg-white border border-gray-200 rounded-lg p-5 transition-all hover:border-purple-200 flex flex-col h-full"
                  >
                    <div className="absolute top-4 right-4 flex gap-2 z-10">
                      <button
                        onClick={() => handleOpenModal(testimonial)}
                        className="p-1.5 text-gray-400 hover:text-[#885ABB] hover:bg-purple-50 rounded-md transition-colors"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(testimonial.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>

                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center text-[#885ABB] font-bold text-xs flex-shrink-0">
                        {testimonial.client.charAt(0)}
                      </div>
                      <div className="overflow-hidden">
                        <h4 className="text-sm font-semibold text-gray-900 truncate pr-14">
                          {testimonial.client}
                        </h4>
                      </div>
                    </div>

                    <div className="relative flex-grow">
                      <Quote
                        size={16}
                        className="absolute -top-1 -left-1 text-purple-100"
                      />
                      <p className="text-xs text-gray-600 italic leading-relaxed pl-4 relative z-0 break-words w-full">
                        "{testimonial.feedback}"
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </WebsiteSectionCard>

        {/* Add/Edit Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
              <div className="bg-[#885ABB] px-6 py-4 flex justify-between items-center">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  {editingId ? <Edit2 size={16} /> : <Plus size={16} />}
                  {editingId ? "Edit Testimonial" : "Add Testimonial"}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-white/80 hover:text-white"
                >
                  <Plus size={20} className="transform rotate-45" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <FormField
                  label="Client Name *"
                  name="clientName"
                  value={clientName}
                  onChange={(_, val) => {
                    setClientName(val);
                    if (errors.clientName) setErrors(prev => ({ ...prev, clientName: "" }));
                  }}
                  type="text"
                  placeholder="e.g. Jane Doe"
                  error={errors.clientName || errors.client}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Feedback <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={feedback}
                    onChange={(e) => {
                      setFeedback(e.target.value);
                      if (errors.feedback) setErrors(prev => ({ ...prev, feedback: "" }));
                    }}
                    className={`w-full px-3 py-2 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 shadow-sm transition-all duration-150 ease-in-out outline-none resize-y min-h-[150px] ${errors.feedback
                      ? 'bg-red-50 border border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500/40'
                      : 'bg-gray-100 border border-transparent focus:border-gray-300 focus:ring-1 focus:ring-purple-500/40 hover:bg-gray-50 focus:bg-white'
                      }`}
                    placeholder="What did they say?"
                  />
                  {errors.feedback && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      {errors.feedback}
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSubmitting}
                    className="bg-[#885ABB] text-white hover:bg-[#7748A4]"
                  >
                    {isSubmitting ? "Saving..." : (editingId ? "Save Changes" : "Add Testimonial")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setTestimonialToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Testimonial"
        message="Are you sure you want to delete this testimonial? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default TestimonialsSection;
